import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, Cpu, Activity, Smartphone, Video, Brain, ExternalLink, BookOpen, Sparkles, Heart, Lock, RefreshCw, Layers, Crown, Rocket, Gamepad2, Microscope, Palette, Code, Briefcase, CheckCircle2, Code2, Database, Users, LayoutTemplate, Sliders, Globe, FileCode, Eye, Download } from 'lucide-react';
import { projectsData } from '../data/projectsData';
import NotFound from './NotFound';
import SEO from '../components/SEO';
import WebsiteTemplatesShowcase from '../components/WebsiteTemplatesShowcase';
import { getSoftwareAppSchema, getBreadcrumbSchema } from '../data/schemas';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projectsData.find(p => p.id === id);

  if (!project) {
    return <NotFound />;
  }

  const projectImage = project.id === 'neurosyn' 
    ? '/neurosyn-icon.png' 
    : project.id === 'tamed' 
      ? '/tamed-icon.webp' 
      : '/lyrika-avatar.webp';

  const projectSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      getSoftwareAppSchema({
        name: project.title,
        description: project.description,
        applicationCategory: project.category,
        operatingSystem: project.id === 'neurosyn' ? 'iOS 17+' : 'Cross-platform, Web, Cloud',
        url: `/projects/${project.id}`,
        codeRepository: project.repoUrl,
        screenshot: projectImage,
        featureList: project.highlights || []
      }),
      getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Projects', url: '/projects' },
        { name: project.title, url: `/projects/${project.id}` }
      ])
    ]
  };

  const getThemeStyles = (theme) => {
    switch (theme) {
      case 'pink':
        return {
          badge: 'bg-pink-950/60 text-pink-300 border-pink-500/40',
          hoverBorder: 'hover:border-pink-500/80 hover:shadow-pink-500/15',
          accentText: 'text-pink-400',
          bullet: 'text-pink-400'
        };
      case 'purple':
        return {
          badge: 'bg-purple-950/60 text-purple-300 border-purple-500/40',
          hoverBorder: 'hover:border-fuchsia-500/80 hover:shadow-fuchsia-500/15',
          accentText: 'text-fuchsia-400',
          bullet: 'text-fuchsia-400'
        };
      case 'emerald':
        return {
          badge: 'bg-purple-950/60 text-fuchsia-300 border-purple-500/40',
          hoverBorder: 'hover:border-purple-400/80 hover:shadow-purple-500/15',
          accentText: 'text-purple-300',
          bullet: 'text-purple-300'
        };
      case 'indigo':
      default:
        return {
          badge: 'bg-purple-950/60 text-purple-200 border-purple-500/40',
          hoverBorder: 'hover:border-fuchsia-500/80 hover:shadow-fuchsia-500/15',
          accentText: 'text-fuchsia-400',
          bullet: 'text-fuchsia-400'
        };
    }
  };

  return (
    <div className="animate-fade-in-down max-w-5xl mx-auto">
      <SEO
        title={`${project.title} — ${project.category}`}
        description={project.description}
        path={`/projects/${project.id}`}
        keywords={[project.title, project.category, 'Koneko', 'NotKoneko', ...(project.tech ? project.tech.split(', ') : [])]}
        image={projectImage}
        schema={projectSchema}
      />
      {/* Back Link */}
      <Link 
        to="/projects" 
        className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 mb-8 transition-colors group"
      >
        <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Directory
      </Link>
      
      {/* Hero Header */}
      <div className="bg-[#090714] p-5 sm:p-8 border border-purple-500/20 rounded-2xl sm:rounded-3xl mb-8 sm:mb-10 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />
        
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 border-b border-purple-500/20 pb-6 relative z-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide mb-2 flex items-center gap-3">
              {project.id === 'neurosyn' && (
                <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden inline-flex items-center justify-center shrink-0 align-middle">
                  <img src="/neurosyn-icon.png" alt="NeuroSyn Icon" className="w-full h-full object-contain rounded-xl" />
                </span>
              )}
              {project.id === 'tamed' && (
                <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden inline-flex items-center justify-center shrink-0 align-middle">
                  <img src="/tamed-icon.webp" alt="Tamed Icon" className="w-full h-full object-contain rounded-xl" />
                </span>
              )}
              {project.id === 'website-templates' && (
                <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-tr from-fuchsia-500/20 via-purple-600/20 to-indigo-600/20 border border-fuchsia-500/40 inline-flex items-center justify-center shrink-0 align-middle shadow-lg shadow-fuchsia-500/15">
                  <LayoutTemplate className="w-5 h-5 sm:w-6 sm:h-6 text-fuchsia-300" />
                </span>
              )}
              {project.title}
            </h1>
            <p className="text-xs sm:text-sm font-mono text-fuchsia-400">{project.tech}</p>
          </div>
          <span className="text-xs font-mono px-3 py-1.5 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded-full flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
            {project.status}
          </span>
        </div>

        <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-3xl font-serif italic relative z-10">
          — {project.description}
        </p>
      </div>

      {/* Custom Section based on project type */}
      {project.bots ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-serif text-white flex items-center gap-3">
              <span className="p-2 bg-[#5865F2]/15 border border-[#5865F2]/40 rounded-xl text-[#5865F2] flex items-center justify-center shadow-lg shadow-[#5865F2]/15">
                <svg className="w-6 h-6 fill-[#5865F2]" viewBox="0 0 127.14 96.36">
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1,105.25,105.25,0,0,0,32.19-16.14c2.64-27.38-4.51-51.11-18.91-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,45.91,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,45.91,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
              </span>
              Bots
            </h2>
            <span className="text-xs font-mono text-gray-400 uppercase tracking-wider">
              Hover to expand preview • Click to view bot page
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 pb-6">
            {project.bots.map((bot) => {
              const styles = getThemeStyles(bot.theme);
              const botSlug = bot.slug || bot.name.toLowerCase().replace(/\s+/g, '-');

              return (
                <Link 
                  key={bot.id}
                  to={`/projects/discordbots/${botSlug}`}
                  className={`group bg-[#090714] hover:bg-[#0e0a1a] p-6 border border-purple-500/20 rounded-2xl flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-2 hover:scale-[1.02] shadow-lg hover:shadow-2xl relative z-0 hover:z-20 ${styles.hoverBorder}`}
                >
                  <div>
                    {/* Tags header */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <span className={`text-xs font-mono px-2.5 py-1 border rounded-md ${styles.badge}`}>
                        {bot.tag}
                      </span>
                      <span className="text-[11px] font-mono px-2 py-0.5 bg-purple-950/40 text-purple-300 rounded border border-purple-500/20">
                        {bot.status}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-pink-400 transition-colors flex items-center justify-between">
                      {bot.name}
                      <span className="text-xs text-gray-500 group-hover:text-pink-400 transition-transform transform group-hover:translate-x-1">
                        →
                      </span>
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                      {bot.description}
                    </p>

                    {/* Features checklist */}
                    {bot.features && (
                      <div className="mb-4 bg-[#0d0a1c] p-3 rounded-lg border border-purple-500/20">
                        <p className="text-[10px] font-mono text-purple-400/90 uppercase tracking-wider mb-2">Key Highlights</p>
                        <ul className="space-y-1">
                          {bot.features.map((feature, idx) => (
                            <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                              <span className={`${styles.bullet} font-bold`}>•</span>
                              <span className="leading-tight">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Expandable Preview on Hover */}
                    {bot.commands && (
                      <div className="max-h-0 opacity-0 group-hover:max-h-48 group-hover:opacity-100 transition-all duration-500 ease-in-out overflow-hidden border-t border-purple-500/20 pt-0 group-hover:pt-3 mt-0 group-hover:mt-3">
                        <p className="text-[10px] font-mono text-pink-400 uppercase tracking-wider mb-2">Available Commands Preview</p>
                        <div className="space-y-1 font-mono text-[11px] text-gray-300 bg-[#0d0a1c] p-2 rounded border border-purple-500/20">
                          {bot.commands.slice(0, 3).map((cmd, idx) => (
                            <div key={idx} className="truncate text-gray-400 group-hover:text-gray-200">
                              <span className="text-pink-400 font-bold">$</span> {cmd}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono mt-4">
                    <span className={styles.accentText}>{bot.tech}</span>
                    <span className="text-gray-400 group-hover:text-pink-400 font-semibold flex items-center gap-1 transition-colors">
                      View Page <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ) : project.id === 'neurosyn' ? (
        <div className="space-y-10 animate-fade-in relative">
          {/* Project Status Banner */}
          <div className="bg-[#090714] p-6 rounded-3xl border border-purple-500/30 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/20 text-purple-300 rounded-2xl border border-purple-500/30 shrink-0">
                  <ShieldCheck className="w-6 h-6 text-fuchsia-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-base font-bold text-white">Private iOS Application Repository</h3>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-purple-900/50 text-purple-300 border border-purple-600/50 rounded">
                      SWIFT 6 ARCHITECTURE
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    Source code and telemetry models are maintained in a private repository prior to official App Store release.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end font-mono text-[10px] text-purple-300 bg-purple-950/40 p-2.5 rounded-xl border border-purple-800/40 shrink-0 w-full md:w-auto">
                <span>Developer: NotKoneko (@Cinnsmp)</span>
                <span className="text-gray-400">Status: Private Beta</span>
              </div>
            </div>
          </div>

          {/* Real Codebase Modules & Architecture */}
          <div className="space-y-4">
            <h2 className="text-2xl font-serif text-white flex items-center gap-2.5">
              <Cpu className="w-6 h-6 text-fuchsia-400" /> Core System Modules &amp; Engineering Pipelines
            </h2>
            <p className="text-sm text-gray-400 leading-relaxed max-w-3xl font-serif italic">
              — NeuroSyn combines low-level Apple frameworks with computational analytics engines to transform raw digital consumption into predictive health telemetry.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 font-mono">
              {/* Module 1: HealthKit Sync */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-fuchsia-500/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] transition-all space-y-3 shadow-lg group">
                <div className="flex items-center justify-between text-[11px] text-fuchsia-400 uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    <span>[ 01 // HEALTHKIT ]</span>
                  </div>
                  <span className="text-[10px] font-mono text-purple-300/80 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">
                    HealthKitManager.swift
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white group-hover:text-fuchsia-300 transition-colors font-sans">
                  HealthKit Biometric Synchronization
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed font-sans">
                  Directly interfaces with Apple HealthKit APIs to extract Heart Rate Variability (HRV), resting pulse rates, active energy expenditure, and multi-stage sleep cycles (Deep, REM, and Core sleep).
                </p>
              </div>

              {/* Module 2: ScreenTime & Activity */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-fuchsia-500/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] transition-all space-y-3 shadow-lg group">
                <div className="flex items-center justify-between text-[11px] text-fuchsia-400 uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    <span>[ 02 // SCREENTIME ]</span>
                  </div>
                  <span className="text-[10px] font-mono text-purple-300/80 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">
                    DeviceActivityManager.swift
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white group-hover:text-fuchsia-300 transition-colors font-sans">
                  Apple ScreenTime &amp; Telemetry Provider
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed font-sans">
                  Utilizes Apple&apos;s native <code className="text-fuchsia-400">DeviceActivity</code> and <code className="text-fuchsia-400">ScreenTime</code> frameworks to record total screen duration, app category frequency, and late-night digital interactions in real-time.
                </p>
              </div>

              {/* Module 3: TikTok Watch History Analyzer */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-pink-500/50 hover:shadow-[0_0_25px_rgba(244,114,182,0.15)] transition-all space-y-3 shadow-lg group">
                <div className="flex items-center justify-between text-[11px] text-pink-400 uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    <span>[ 03 // MEDIA TELEMETRY ]</span>
                  </div>
                  <span className="text-[10px] font-mono text-pink-300/80 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">
                    TikTokWatchHistoryAnalyzer.swift
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white group-hover:text-pink-300 transition-colors font-sans">
                  TikTok Watch History Telemetry Engine
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed font-sans">
                  Processes user watch histories, scroll speed density, and temporal activity logs using <code className="text-pink-400">SleepDetection.swift</code> to isolate late-night media consumption spikes and dopamine fatigue patterns.
                </p>
              </div>

              {/* Module 4: Insight Correlation Engine */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-purple-400/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] transition-all space-y-3 shadow-lg group">
                <div className="flex items-center justify-between text-[11px] text-purple-400 uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    <span>[ 04 // PROTOCOL CORE ]</span>
                  </div>
                  <span className="text-[10px] font-mono text-purple-300/80 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">
                    InsightCorrelationEngine.swift
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white group-hover:text-purple-200 transition-colors font-sans">
                  Insight Correlation &amp; Sleep Calculation
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed font-sans">
                  Runs mathematical correlation algorithms via <code className="text-purple-300">SleepCalculationEngine.swift</code> to calculate predictive sleep disruption scores, cognitive readiness metrics, and recovery recommendations.
                </p>
              </div>
            </div>
          </div>

          {/* Native SwiftUI User Interface Architecture */}
          <div className="bg-[#090714] p-6 sm:p-8 rounded-3xl border border-purple-500/20 space-y-6 shadow-xl">
            <div>
              <h2 className="text-xl font-serif text-white flex items-center gap-2 mb-2">
                <img src="/neurosyn-icon.png" alt="NeuroSyn Icon" className="w-5 h-5 object-contain inline-block" /> Native SwiftUI Dashboard Architecture
              </h2>
              <p className="text-xs text-gray-400 leading-relaxed font-mono">
                The iOS application user experience is crafted using modular SwiftUI components engineered for 120Hz Apple ProMotion displays.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs">
              <div className="p-3 bg-[#0d0a1c] border border-purple-500/20 rounded-xl text-center">
                <span className="text-fuchsia-400 block font-bold mb-1">HomeDashboard</span>
                <span className="text-[10px] text-gray-400 block">Primary Overview</span>
              </div>
              <div className="p-3 bg-[#0d0a1c] border border-purple-500/20 rounded-xl text-center">
                <span className="text-pink-400 block font-bold mb-1">SleepDashboard</span>
                <span className="text-[10px] text-gray-400 block">Biometric Sleep View</span>
              </div>
              <div className="p-3 bg-[#0d0a1c] border border-purple-500/20 rounded-xl text-center">
                <span className="text-purple-300 block font-bold mb-1">ScoringRingView</span>
                <span className="text-[10px] text-gray-400 block">Cognitive Readiness</span>
              </div>
              <div className="p-3 bg-[#0d0a1c] border border-purple-500/20 rounded-xl text-center">
                <span className="text-purple-200 block font-bold mb-1">SleepStageBar</span>
                <span className="text-[10px] text-gray-400 block">REM / Deep Visualizer</span>
              </div>
            </div>
          </div>

          {/* System Specification Notice */}
          <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 font-mono text-xs text-gray-400 leading-relaxed shadow-lg">
            <span className="text-fuchsia-400 font-bold block mb-1">// System Protection &amp; IP Notice</span>
            All underlying source code, binary compilation targets, and mathematical correlation weights are protected under the official NeuroSyn Asset Provenance Manifest. App Store release schedules and beta testing invitations will be shared publicly upon completion of validation testing.
          </div>
        </div>
      ) : project.id === 'tamed' ? (
        <div className="space-y-8 animate-fade-in relative">
          {/* Hero Banner */}
          <div className="bg-[#090714] p-6 sm:p-8 rounded-3xl border border-purple-500/30 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-3 max-w-xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/80 border border-purple-500/40 text-purple-300 text-[11px] font-mono font-bold tracking-wider">
                  <Briefcase className="w-3 h-3 text-fuchsia-400" /> CLIENT COMMISSION
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-white leading-tight">
                  Gamified Routine &amp;<br />Relationship Tracker
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  A full-stack accountability platform with dual-role dashboards, an XP &amp; reward economy, real-time Supabase sync, and cryptographic pairing — deployed on Cloudflare Pages.
                </p>
              </div>

              <div className="flex flex-col gap-3 w-full md:w-auto shrink-0">
                <a
                  href="https://tamed.pages.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl text-xs font-mono flex items-center justify-center gap-2 shadow-lg shadow-purple-600/30 transition-all hover:scale-105"
                >
                  <ExternalLink className="w-4 h-4" /> Launch App
                </a>
                <a
                  href="https://github.com/NottKoneko/Tamed"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-300 font-semibold rounded-xl text-xs font-mono border border-purple-500/20 flex items-center justify-center gap-2 transition-all"
                >
                  <Code2 className="w-4 h-4 text-purple-400" /> GitHub Repo
                </a>
              </div>
            </div>

            {/* Tech Stats Strip */}
            <div className="relative z-10 mt-6 pt-5 border-t border-purple-500/20 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Frontend', value: 'React 19 + Vite 6', color: 'text-fuchsia-300' },
                { label: 'State', value: 'Zustand 5', color: 'text-purple-300' },
                { label: 'Backend', value: 'Supabase + RLS', color: 'text-pink-300' },
                { label: 'Deploy', value: 'Cloudflare Pages', color: 'text-purple-200' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <span className={`text-xs font-mono font-bold ${color} block`}>{value}</span>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5 block">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dual Role System */}
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-serif text-white flex items-center gap-2.5 mb-1">
                <Users className="w-5 h-5 text-fuchsia-400" /> Dual-Role Architecture
              </h2>
              <p className="text-sm text-gray-400 max-w-2xl font-mono text-xs">
                Two specialized dashboards linked via a cryptographic 6-digit pair key — each tailored to a distinct user role.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Manager Card */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-purple-400/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] transition-all duration-300 space-y-4 group relative overflow-hidden">
                <div className="flex items-center gap-3 relative z-10">
                  <div className="p-2.5 bg-purple-500/15 border border-purple-500/30 rounded-xl">
                    <Crown className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <div>
                    <span className="text-[10px] font-mono text-fuchsia-400 uppercase tracking-widest block">Manager Role</span>
                    <h3 className="text-base font-bold text-white">Administration &amp; Oversight</h3>
                  </div>
                </div>
                <ul className="space-y-2.5 text-xs text-gray-400 relative z-10 font-mono">
                  {[
                    ['Daily Routine Builder', 'Create custom tasks with configurable XP weightings per completion.'],
                    ['Discipline Logging', 'Track quality history with Green / Yellow / Red status markers.'],
                    ['Reward Store Admin', 'Manage catalog items and review partner redemption requests.'],
                    ['Praise & Nudge Engine', 'Send instant praise cards and check-in reminders in one click.'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-2">
                      <span className="text-fuchsia-400 font-bold shrink-0 mt-0.5">›</span>
                      <span><span className="text-gray-200 font-semibold">{title}:</span> {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Partner Card */}
              <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 hover:border-pink-500/50 hover:shadow-[0_0_25px_rgba(244,114,182,0.15)] transition-all duration-300 space-y-4 group relative overflow-hidden">
                <div className="flex items-center gap-3 relative z-10">
                  <div className="p-2.5 bg-pink-500/15 border border-pink-500/30 rounded-xl">
                    <img src="/tamed-icon.webp" alt="Tamed" className="w-5 h-5 object-contain" />
                  </div>
                  <div>
                    <span className="text-[10px] font-mono text-pink-400 uppercase tracking-widest block">Partner Role</span>
                    <h3 className="text-base font-bold text-white">Gamified Progression</h3>
                  </div>
                </div>
                <ul className="space-y-2.5 text-xs text-gray-400 relative z-10 font-mono">
                  {[
                    ['XP & Leveling', 'Earn +25 XP per completed task, level up, and unlock milestone titles.'],
                    ['Custom Currencies', 'Accumulate avatar-specific points: Bones, Fish, Berries, or Stars.'],
                    ['Midnight Auto-Reset', 'Daily checklists wipe automatically at midnight for fresh starts.'],
                    ['Store Redemptions', 'Redeem earned points for rewards or propose custom items.'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-2">
                      <span className="text-pink-400 font-bold shrink-0 mt-0.5">›</span>
                      <span><span className="text-gray-200 font-semibold">{title}:</span> {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Engineering Highlights */}
          <div className="space-y-4">
            <h2 className="text-xl font-serif text-white flex items-center gap-2.5">
              <Cpu className="w-5 h-5 text-purple-300" /> Engineering Highlights
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs">
              {[
                {
                  icon: <RefreshCw className="w-4 h-4 text-fuchsia-400" />,
                  bg: 'bg-purple-500/10 border-purple-500/20',
                  title: 'Hybrid State Sync',
                  desc: 'Zustand 5 manages UI state with Supabase realtime WebSocket subscriptions. Offline demo mode available without auth.',
                },
                {
                  icon: <Lock className="w-4 h-4 text-pink-400" />,
                  bg: 'bg-pink-500/10 border-pink-500/20',
                  title: 'Security Stack',
                  desc: '6-digit cryptographic pair keys, PIN lockout rate limiters, XSS sanitization, and Supabase Row-Level Security policies.',
                },
                {
                  icon: <Layers className="w-4 h-4 text-purple-300" />,
                  bg: 'bg-purple-500/10 border-purple-500/20',
                  title: 'Edge Deployment',
                  desc: 'React 19 SPA bundled with Vite 6 and deployed globally on Cloudflare Pages edge infrastructure.',
                },
              ].map(({ icon, bg, title, desc }) => (
                <div key={title} className="bg-[#090714] p-5 rounded-2xl border border-purple-500/20 hover:border-fuchsia-500/40 transition-all space-y-3 shadow-lg">
                  <div className={`w-fit p-2.5 border rounded-xl ${bg}`}>{icon}</div>
                  <h4 className="text-sm font-bold text-white font-sans">{title}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-sans">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Wiki Documentation */}
          <div className="bg-[#090714] p-6 sm:p-7 rounded-3xl border border-purple-500/20 space-y-4 shadow-xl">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-base font-serif text-white flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-fuchsia-400" /> Project Documentation
              </h3>
              <a
                href="https://github.com/NottKoneko/Tamed/wiki"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-fuchsia-400 hover:text-fuchsia-300 hover:underline flex items-center gap-1 transition-colors"
              >
                View Full Wiki →
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { href: 'https://github.com/NottKoneko/Tamed/wiki/User-Onboarding-Guide', icon: <Rocket className="w-3.5 h-3.5 text-purple-400 shrink-0" />, label: 'Onboarding & Pair Auth', color: 'hover:border-purple-500/50' },
                { href: 'https://github.com/NottKoneko/Tamed/wiki/Gamification-and-Reward-System', icon: <Gamepad2 className="w-3.5 h-3.5 text-pink-400 shrink-0" />, label: 'XP, Levels & Currencies', color: 'hover:border-pink-500/50' },
                { href: 'https://github.com/NottKoneko/Tamed/wiki/Security-and-Privacy', icon: <ShieldCheck className="w-3.5 h-3.5 text-fuchsia-400 shrink-0" />, label: 'Security & RLS Rules', color: 'hover:border-fuchsia-500/50' },
                { href: 'https://github.com/NottKoneko/Tamed/wiki/Architecture-and-Database', icon: <Database className="w-3.5 h-3.5 text-purple-300 shrink-0" />, label: 'Architecture & Schema', color: 'hover:border-purple-500/50' },
                { href: 'https://github.com/NottKoneko/Tamed/wiki/Customization-and-Themes', icon: <Palette className="w-3.5 h-3.5 text-pink-400 shrink-0" />, label: 'CSS Themes & Variables', color: 'hover:border-pink-500/50' },
                { href: 'https://github.com/NottKoneko/Tamed/wiki/Development-and-Deployment', icon: <Code className="w-3.5 h-3.5 text-purple-200 shrink-0" />, label: 'Vite Build & Deployment', color: 'hover:border-purple-500/50' },
              ].map(({ href, icon, label, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3.5 bg-[#0d0a1c] border border-purple-500/20 ${color} rounded-xl text-gray-400 hover:text-white transition-all flex items-center gap-2.5 text-xs font-mono group`}
                >
                  {icon}
                  <span className="group-hover:translate-x-0.5 transition-transform">{label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : project.id === 'website-templates' ? (
        <WebsiteTemplatesShowcase project={project} />
      ) : (
        <div className="bg-[#090714] p-8 border border-purple-500/20 rounded-2xl text-gray-400 font-mono text-sm">
          Detailed breakdown coming soon...
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;